import { Compiler, Component, HostBinding, Injector, OnInit } from '@angular/core'
import { Platform } from '@ionic/angular'
import Measured from '@measured-im/browser'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { ImageGameService } from 'src/app/services/image-game/image-game.service'
import { KakaoService } from 'src/app/services/kakao/kakao.service'
import { LogService } from 'src/app/services/log/log.service'
import { ModalService } from 'src/app/services/modal/modal.service'
import { QuizService } from 'src/app/services/quiz/quiz.service'
import { environment } from 'src/environments/environment'
import { customLocalStorage } from 'src/utils/utils'
import packageInfo from '../../package.json'
import { PageLoadingContent } from './common/components/page-loading/page-loading.component'
import { TermAgreementComponent } from './common/modals/term-agreement/term-agreement.component'
import { KakaoSyncTerms } from './interfaces/kakao'
import { LocaleCode } from './interfaces/locale'
import { LogEvent } from './interfaces/log'
import { DailyLogService } from './services/daily-log/daily-log.service'
import { Event } from './services/event/event.service'
import { GeneralService } from './services/general/general.service'
import { LocaleService } from './services/locale/locale-service'
import { MbtiService } from './services/mbti/mbti.service'
import { RouterService } from './services/router/router.service'
import { SajuService } from './services/saju/saju.service'
import { UserService } from './services/user/user.service'
import { WebviewService } from './services/webview/webview.service'

// 앱 점검을 위해 asset file에서 import 해옴
declare const isUnderSystemCheck: boolean
declare const window: any

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  @HostBinding('style.--gnb-tab-height') gnbTabHeight

  constructor(
    private platform: Platform,
    private routerService: RouterService,
    private userService: UserService,
    private generalService: GeneralService,
    private logService: LogService,
    private modalService: ModalService,
    private kakaoService: KakaoService,
    private compiler: Compiler,
    private injector: Injector,
    private quizService: QuizService,
    private event: Event<void>,
    private webviewService: WebviewService,
    private imageGameService: ImageGameService,
    private mbtiService: MbtiService,
    private localeService: LocaleService,
    private dailyLogService: DailyLogService,
    private sajuService: SajuService,
    private translate: TranslateService
  ) {
    this.uuid = UserService.generateUUID()
    this.initializeCssVariable()
    this.translate.setDefaultLang(this.localeService.locale)
  }

  isUpdating = false
  updateInstalled = false
  updateFailed = false
  androidStatusBarColor: string | undefined
  globalPageLoading = false
  globalPageLoadingContent: PageLoadingContent | undefined
  globalPageLoadingSubscription: Subscription | undefined
  private fromKakaoLoginCallback = false
  private isKakaoErrorCallback = false
  private uuid: string | undefined

  get updateStatus(): 'updating' | 'updateInstalled' | 'failed' | undefined {
    if (this.isUpdating) {
      return 'updating'
    }
    if (this.updateInstalled) {
      return 'updateInstalled'
    }
    if (this.updateFailed) {
      return 'failed'
    }
  }

  // statusbar 전용
  get onUpdateProcess(): boolean {
    return this.updateStatus === 'updating' || this.updateStatus === 'updateInstalled'
  }

  async ngOnInit(): Promise<void> {
    Measured.install(environment.measuredApiKey)
    this.webviewService.init()
    const [enteringPath, queryParams] = this.initializeUrl()
    await this.logService.init()
    // 반드시 userService init 완료 후에 initializeApp 함수가 실행되어야 함
    await this.userService.init()
    this.logService.setSession()
    this.logService.setUserConfig({
      versionSelfDefined: packageInfo.version,
    })
    this.logService.eventLogging(LogEvent.APP_OPENED)

    await this.logService.exp.setExperimentalVariables()

    // 점검 중인 경우, 점검 화면만 띄워주고 앱 접근 불가능
    if (isUnderSystemCheck) {
      this.routerService.navigateToErrorPage('SYSTEM_CHECK')
      return
    }
    this.checkUserSignedKakaoSync()
    this.initializeApp()
    this.subscribeGlobalLoading()
    this.loadLazyLoadingModules()
    // initialize locale
    let languageCode: LocaleCode | undefined
    if (this.userService.isLoggedIn) {
      const res = await this.userService.getMyPrivateData().toPromise()
      languageCode = res?.data?.languageCode
    }
    this.localeService.setInitialLocale(this.userService.isLoggedIn, languageCode)
    const lang = this.translate.currentLang || 'ko' // 기본값은 한국어
    document.documentElement.lang = this.localeService.locale // <html> 태그의 lang 속성 변경
    // update user language code to server
    if (this.userService.isLoggedIn) {
      this.userService.updateUserPrivateData({ languageCode: this.localeService.locale }).subscribe()
    }
    if (this.localeService.locale === LocaleCode.ja) {
      this.loadJaFont()
    }
  }

  private initializeUrl(): [string, { [key: string]: string }] {
    let url = window.location.href
    this.logService.setUtms(url)
    this.userService.setTestGroupFromUrl(url)
    this.logService.setUserConfig()
    if (url.includes('oauth/kakao')) {
      this.fromKakaoLoginCallback = true
      if (url.includes('error_description')) {
        this.isKakaoErrorCallback = true
      }
    }
    if (url.includes('types.headmost.app')) {
      url = url.replace('types.headmost.app', 'types.my')
    }
    if (location.pathname.startsWith('/my')) {
      url = url.replace('/my', '/profile')
    }
    if (url !== window.location.href) {
      window.location.href = url
    }
    const enteringPath = window.location.pathname
    const enteringQueryParams = new URLSearchParams(window.location.search)
    const queryParams: { [key: string]: string } = {}
    enteringQueryParams.forEach((value, key) => {
      queryParams[key] = value
    })
    return [enteringPath, queryParams]
  }

  private checkUserSignedKakaoSync(): void {
    // kakao callback일 경우, 바로 api call 다시 할 없어서 이하 스킵.
    // but, kakao callback error 발생시 이하 재수행
    if (!this.userService.isLoggedIn || (this.fromKakaoLoginCallback && !this.isKakaoErrorCallback)) {
      return
    }
    // 구글/애플 로그인 유저는 kakao sync 체크 안함
    if (!this.userService.initData.isKakaoUser) return
    this.kakaoService.getKakaoUserServiceTerms().subscribe(
      (terms: KakaoSyncTerms) => {
        const notAllowedServiceTerms: string[] = this.getNotAllowedServiceTerms(terms)
        const termAgreed = this.isTermAgreed(terms)
        const kakaoSyncLocalStorageKey = 'kakao_sync_started'
        !termAgreed && this.handleTermNotAgreedState(kakaoSyncLocalStorageKey, notAllowedServiceTerms)
      },
      (error) => {
        console.error(error)
      }
    )
  }

  private handleTermNotAgreedState(kakaoSyncLocalStorageKey: string, notAllowedServiceTerms: string[]): void {
    customLocalStorage.setItem(kakaoSyncLocalStorageKey, 'true')
    this.kakaoSyncForTermNotAgreedUsers(notAllowedServiceTerms)
  }

  private getNotAllowedServiceTerms(terms: KakaoSyncTerms): string[] {
    const { allowedServiceTerms, appServiceTerms } = terms
    const allowedTerms = Array.isArray(allowedServiceTerms) ? this.getTerms(allowedServiceTerms) : []
    const result = this.getTerms(appServiceTerms).filter((term) => !allowedTerms.includes(term))
    return result
  }

  private isTermAgreed(terms: KakaoSyncTerms): boolean {
    const { allowedServiceTerms, appServiceTerms } = terms
    let userSynced = false
    if (Array.isArray(allowedServiceTerms) && allowedServiceTerms.length > 0 && Array.isArray(appServiceTerms)) {
      userSynced = true
      const requiredAllowedTerms = this.getTerms(allowedServiceTerms)
      const requiredAppTerms = this.getTerms(appServiceTerms)
      requiredAppTerms.forEach((appTerm) => {
        if (!requiredAllowedTerms.includes(appTerm)) {
          // kakao에서 필수항목 여부를 안알려줘서, tag name뒤에 '_required'를 붙여 구분
          if (appTerm.includes('_required')) {
            userSynced = false
          }
        }
      })
    }

    return userSynced
  }

  private getTerms(terms: { tag: string }[]): string[] {
    return terms.map((term) => term.tag)
  }

  /** 기존유저 or 약관 비동의 유저에게 kakao sync 유도 */
  private async kakaoSyncForTermNotAgreedUsers(notAllowedServiceTerms: string[]): Promise<void> {
    const modal = await this.modalService.openBottomSheet(
      TermAgreementComponent,
      {
        notAllowedServiceTerms,
      },
      false
    )
    await modal.present()
  }

  private initializeApp(): void {
    this.addPlatformSubscriptions()

    if (!this.kakaoService.isKakaoInitialized) {
      this.kakaoService.initializeKakao()
    }
  }

  private addPlatformSubscriptions(): void {
    this.addPlatformReadySubscription()
  }

  private addPlatformReadySubscription(): void {
    this.platform.ready().then(async () => {
      this.routerService.addRouterHistoryTracker()
      if (this.userService.initData.isLoggedIn) {
        this.initializeForLoggedInUser()
        // 로그인 한 유저의 경우 localStorage에 저장된 응답 기록들이 남아있는 경우, 저장시키기
        this.quizService.connectDataToLoginUser()
        this.imageGameService.connectImageGames()
        if (this.uuid) {
          this.sajuService.connectAnonymousSaju(this.uuid)
          this.dailyLogService.connectAnonymousDailyLog(this.uuid)
        }
      } else {
        this.userService.isLoggedIn$.subscribe((isLoggedIn) => {
          if (isLoggedIn && this.uuid) {
            this.sajuService.connectAnonymousSaju(this.uuid)
            this.dailyLogService.connectAnonymousDailyLog(this.uuid)
          }
        })
      }
    })
  }

  private initializeCssVariable(): void {
    this.gnbTabHeight = '0px'
  }

  /** 로그인 유저일 경우 필요한 데이터 불러오기 */
  private initializeForLoggedInUser(): void {
    // 유저 프로필 정보
    this.userService.getProfile().subscribe((res) => {
      // 유저 서베이 정보
      this.userService.getSurveyProperty().subscribe()
      // 유저 mbti 정보
      this.userService.getMbtiProperty().subscribe()
      // 유저 애착유형 정보
      this.userService.getAttachmentTypeProperty().subscribe()
      if (res?.data?.profile?.mbtiType) {
        this.mbtiService.myMbti = res.data.profile.mbtiType.mbtiType
      } else {
        this.mbtiService.getMyMbtiHostQuizReportList(null).subscribe()
      }
    })
    // 유저 알림 설정 정보
    this.userService.getNotificationSettingData().subscribe()
    // 공지 배너 목록 가져오기
    this.generalService.getNoticeList().subscribe()
  }

  private subscribeGlobalLoading(): void {
    this.globalPageLoadingSubscription = this.generalService
      .getGlobalPageLoadingSubject()
      .subscribe(({ showLoading, content }) => {
        this.globalPageLoading = showLoading
        this.globalPageLoadingContent = content
      })
  }

  private loadLazyLoadingModules(): void {
    this.loadMyModule()
    this.loadFavourTournamentModule()
    this.loadAnswerModule()
    this.loadLoginModule()
    this.loadQuizModule()
    this.loadSettingModule()
    this.loadChemiModule()
    this.loadProfileModule()
  }

  private loadMyModule(): void {
    import('./pages/my/my.module')
      .then((m) => m.MyModule)
      .then((module) => this.compiler.compileModuleAsync(module))
      .then((factory) => factory.create(this.injector))
  }

  private loadFavourTournamentModule(): void {
    import('./pages/favour-tournament/favour-tournament.module')
      .then((m) => m.FavourTournamentModule)
      .then((module) => this.compiler.compileModuleAsync(module))
      .then((factory) => factory.create(this.injector))
  }

  private loadAnswerModule(): void {
    import('./pages/answer/answer.module')
      .then((m) => m.AnswerModule)
      .then((module) => this.compiler.compileModuleAsync(module))
      .then((factory) => factory.create(this.injector))
  }

  private loadLoginModule(): void {
    import('./pages/login/login.module')
      .then((m) => m.LoginModule)
      .then((module) => this.compiler.compileModuleAsync(module))
      .then((factory) => factory.create(this.injector))
  }

  private loadQuizModule(): void {
    import('./pages/quiz/quiz.module')
      .then((m) => m.QuizModule)
      .then((module) => this.compiler.compileModuleAsync(module))
      .then((factory) => factory.create(this.injector))
  }

  private loadSettingModule(): void {
    import('./pages/setting/setting.module')
      .then((m) => m.SettingModule)
      .then((module) => this.compiler.compileModuleAsync(module))
      .then((factory) => factory.create(this.injector))
  }

  private loadChemiModule(): void {
    import('./pages/chemi/chemi.module')
      .then((m) => m.ChemiModule)
      .then((module) => this.compiler.compileModuleAsync(module))
      .then((factory) => factory.create(this.injector))
  }

  private loadProfileModule(): void {
    import('./pages/profile/profile.module')
      .then((m) => m.ProfileModule)
      .then((module) => this.compiler.compileModuleAsync(module))
      .then((factory) => factory.create(this.injector))
  }

  private loadJaFont(): void {
    /* Pretendard-JP은 용량이 커서 동적로드함 */
    const fontVariants = [
      {
        url: 'assets/fonts/Pretendard-JP/PretendardJP-Regular.woff2',
        weight: '400',
      },
      {
        url: 'assets/fonts/Pretendard-JP/PretendardJP-Medium.woff2',
        weight: '500',
      },
      {
        url: 'assets/fonts/Pretendard-JP/PretendardJP-SemiBold.woff2',
        weight: '600',
      },
      {
        url: 'assets/fonts/Pretendard-JP/PretendardJP-Bold.woff2',
        weight: '700',
      },
      {
        url: 'assets/fonts/Pretendard-JP/PretendardJP-ExtraBold.woff2',
        weight: '800',
      },
    ]

    Promise.all(
      fontVariants.map(async (variant) => {
        const font = new FontFace('Pretendard-JP', `url(${variant.url})`, {
          weight: variant.weight,
          style: 'normal',
        })
        const loaded = await font.load()
        ;(document.fonts as FontFaceSet).add(loaded)
      })
    )
  }
}
